// Smooth line ("wave") chart shared by the Funnel Monitoring sections' Wave view.
import React, { useMemo } from "react";
import {
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  Plugin,
  PointElement,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

export interface WaveSeries {
  label: string;
  data: number[];
  color?: string;
  // Optional per-point colour for the value labels (e.g. critical values).
  valueColors?: string[];
}

interface WaveChartProps {
  // X-axis labels; multi-word labels can be passed as string[] to wrap.
  labels: Array<string | string[]>;
  series: WaveSeries[];
  isPercent?: boolean;
  // Write each point's value above it (used for single-line charts).
  showValues?: boolean;
  // Extra line(s) under the hover list for a given x index.
  tooltipFooter?: (index: number) => string | undefined;
  tooltipTitle?: (label: string) => string;
  height?: number;
  // Legend on the right; clicking a name hides or shows its line.
  showLegend?: boolean;
  // "index": hover lists every line at that x. "point": only the line under the pointer.
  hoverMode?: "index" | "point";
}

// Colour-blind-safe categorical palette (validated). Colour follows the series position, so
// it never changes when other series have no data; past eight the palette repeats dashed.
const PALETTE = [
  "#2a78d6",
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#008300",
  "#4a3aa7",
  "#e34948",
];

// Axis labels in dark slate so hours, stages and values read clearly.
const AXIS_TEXT_COLOR = "#334155";
const AXIS_FONT = { size: 12, weight: "500" } as const;

type WaveDataset = {
  isIdle?: boolean;
  valueColors?: string[];
  showValues?: boolean;
};

const formatValue = (value: number, isPercent: boolean): string =>
  isPercent ? `${value.toFixed(2)}%` : value.toLocaleString();

// Draws each point's value above it for datasets that ask for it.
const createValueLabelsPlugin = (isPercent: boolean): Plugin<"line"> => ({
  id: "waveValueLabels",
  afterDatasetsDraw: (chart) => {
    const { ctx } = chart;
    ctx.save();
    ctx.font = "600 12px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const extra = dataset as unknown as WaveDataset;
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!extra.showValues || meta.hidden) {
        return;
      }
      meta.data.forEach((point, index) => {
        const value = Number(dataset.data[index] ?? 0);
        const { x, y } = point.getProps(["x", "y"], true);
        ctx.fillStyle = extra.valueColors?.[index] ?? "#0f172a";
        ctx.fillText(formatValue(value, isPercent), x, y - 8);
      });
    });
    ctx.restore();
  },
});

export const WaveChart: React.FC<WaveChartProps> = React.memo(
  ({
    labels,
    series,
    isPercent = false,
    showValues = false,
    tooltipFooter,
    tooltipTitle,
    height = 320,
    showLegend = false,
    hoverMode = "index",
  }) => {
    const chartData = useMemo<ChartData<"line">>(
      () => ({
        labels,
        datasets: series.map((item, index) => {
          const isIdle = item.data.every((value) => value === 0);
          const color = item.color ?? PALETTE[index % PALETTE.length];
          // A series that is 0 everywhere draws nothing but stays in the hover list.
          const hideIdle = isIdle && series.length > 1;
          return {
            label: item.label,
            data: item.data,
            isIdle: hideIdle,
            showValues,
            valueColors: item.valueColors,
            borderColor: color,
            backgroundColor: color,
            borderWidth: 2,
            borderDash: !item.color && index >= PALETTE.length ? [6, 4] : [],
            // Repeated colours also get a diamond marker, in the legend and on the points.
            pointStyle:
              !item.color && index >= PALETTE.length ? "rectRot" : "circle",
            cubicInterpolationMode: "monotone" as const,
            tension: 0.4,
            showLine: !hideIdle,
            pointRadius: hideIdle ? 0 : 3,
            pointHoverRadius: hideIdle ? 0 : 5,
            // Lines with no data can't be hovered; real points get a slightly larger target.
            pointHitRadius: hideIdle ? 0 : 6,
          };
        }),
      }),
      [labels, series, showValues],
    );

    const options = useMemo<ChartOptions<"line">>(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction:
          hoverMode === "point"
            ? { mode: "nearest", intersect: true }
            : { mode: "index", intersect: false },
        layout: { padding: { top: showValues ? 22 : 4 } },
        plugins: {
          legend: showLegend
            ? {
                position: "right",
                align: "start",
                labels: {
                  usePointStyle: true,
                  boxWidth: 8,
                  boxHeight: 8,
                  padding: 10,
                  color: AXIS_TEXT_COLOR,
                },
              }
            : { display: false },
          tooltip: {
            backgroundColor: "#ffffff",
            titleColor: "#0f172a",
            bodyColor: "#334155",
            footerColor: "#334155",
            borderColor: "#e2e8f0",
            borderWidth: 1,
            padding: 10,
            usePointStyle: true,
            boxWidth: 8,
            boxHeight: 8,
            callbacks: {
              title: (items) => {
                // Use the original axis label: Chart.js joins wrapped (array) labels with
                // commas in items[].label, so join the lines with spaces instead.
                const first = items[0];
                const raw = first
                  ? first.chart.data.labels?.[first.dataIndex]
                  : "";
                const label = Array.isArray(raw)
                  ? raw.join(" ")
                  : String(raw ?? "");
                return tooltipTitle ? tooltipTitle(label) : label;
              },
              label: (item) =>
                `${item.dataset.label}: ${formatValue(Number(item.raw), isPercent)}`,
              footer: (items) =>
                tooltipFooter
                  ? (tooltipFooter(items[0]?.dataIndex ?? 0) ?? "")
                  : "",
            },
          },
        },
        scales: {
          x: {
            // Half-step padding keeps the first and last points off the chart edges.
            offset: true,
            grid: { display: false },
            ticks: {
              color: AXIS_TEXT_COLOR,
              font: AXIS_FONT,
              autoSkip: false,
              maxRotation: 0,
            },
          },
          y: {
            beginAtZero: true,
            border: { display: false },
            grid: { color: "#f1f5f9" },
            ticks: isPercent
              ? {
                  color: AXIS_TEXT_COLOR,
                  font: AXIS_FONT,
                  callback: (value) => `${value}%`,
                }
              : { color: AXIS_TEXT_COLOR, font: AXIS_FONT, precision: 0 },
          },
        },
      }),
      [
        isPercent,
        showValues,
        tooltipFooter,
        tooltipTitle,
        showLegend,
        hoverMode,
      ],
    );

    const plugins = useMemo(
      () => [createValueLabelsPlugin(isPercent)],
      [isPercent],
    );

    if (labels.length === 0) {
      return null;
    }

    return (
      <div className="funnel-minimal-wave-chart" style={{ height }}>
        <Line data={chartData} options={options} plugins={plugins} />
      </div>
    );
  },
);
